import React, { useState, useMemo, useEffect } from "react";
import { Box, useMediaQuery } from "@mui/material";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { useNavigate } from "react-router-dom";
import { Machine, MachineType } from "../../machinery/models/models";
import { ManufacturerReference, CompanyMachinesQuery } from "../models/models";
import CompactMachineCard from "../../machinery/components/CompactMachineCard";
import useTranslation from "../../../shared/hooks/useTranslation";
import { dt } from "../../../shared/themes/redesignTokens";
import { useAppDispatch } from "../../../shared/hooks/sharedHooks";
import {
  getMachines,
  changeMachinesQuery,
  getMachineTypes,
} from "../customerCompaniesSlice";

// ===================================================================
//  Types
// ===================================================================

interface CompanyMachineryProps {
  machines: Record<string, Machine[]>;
  machineTypes: MachineType[];
  machineManufacturers: ManufacturerReference[];
  machinesQuery: CompanyMachinesQuery;
  companyId: string;
  customerType?: number;
}

const MAX_DISPLAY = 12;

// ===================================================================
//  Component
// ===================================================================

const CompanyMachinery = ({
  machines,
  machineTypes,
  machineManufacturers,
  machinesQuery,
  companyId,
  customerType,
}: CompanyMachineryProps) => {
  const { translate } = useTranslation("partnerHub");
  const isPhone = useMediaQuery("(max-width:768px)");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string | null>(null);

  // Only render for Machine Owners (1) or Hybrid (3)
  const isMachineOwner = customerType === 1 || customerType === 3;

  // Load machine types on mount
  useEffect(() => {
    if (companyId) {
      const newQuery = { ...machinesQuery, customerId: companyId };
      dispatch(changeMachinesQuery(newQuery));
      dispatch(getMachines(newQuery));
      dispatch(getMachineTypes());
    }
  }, [companyId]);

  // Flatten machines from grouped Record to flat array
  const allMachines = useMemo(() => {
    const flat: Machine[] = [];
    for (const group of Object.values(machines)) {
      flat.push(...group);
    }
    return flat;
  }, [machines]);

  // Apply search + type filter
  const filteredMachines = useMemo(() => {
    let result = allMachines;
    if (search.trim()) {
      const s = search.toLowerCase();
      result = result.filter(
        (m) =>
          (m.model || "").toLowerCase().includes(s) ||
          (m.customBrandName || "").toLowerCase().includes(s) ||
          (m.manufacturer?.name || "").toLowerCase().includes(s) ||
          (m.serialNumber || "").toLowerCase().includes(s)
      );
    }
    if (typeFilter) {
      result = result.filter((m) => m.type?.id === typeFilter);
    }
    return result;
  }, [allMachines, search, typeFilter]);

  const totalCount = allMachines.length;
  const displayCount = filteredMachines.length;
  const isCapped = displayCount > MAX_DISPLAY;

  // Cap at MAX_DISPLAY for display
  const displayedMachines = isCapped
    ? filteredMachines.slice(0, MAX_DISPLAY)
    : filteredMachines;

  // Available type filters from actual machine data
  const typeFilters = useMemo(() => {
    const seen = new Map<string, string>();
    for (const m of allMachines) {
      if (m.type?.id && m.type?.name && !seen.has(m.type.id)) {
        seen.set(m.type.id, m.type.name);
      }
    }
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [allMachines]);

  if (!isMachineOwner) return null;

  return (
    <Box
      sx={{
        background: dt.cardBg,
        borderRadius: dt.radiusSm,
        border: `1px solid ${dt.cardBorder}`,
        boxShadow: dt.cardShadow,
        overflow: "hidden",
        animation: "fadeUp 0.4s cubic-bezier(0.4,0,0.2,1) 0.16s both",
      }}
    >
      {/* Gradient accent strip */}
      <Box
        sx={{
          height: "2px",
          background: `linear-gradient(90deg, ${dt.blue}, ${dt.blueHover}, ${dt.blue}00)`,
        }}
      />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: isPhone ? "flex-start" : "center",
          flexDirection: isPhone ? "column" : "row",
          gap: "12px",
          padding: isPhone ? "16px" : "16px 24px",
          borderBottom: `1px solid ${dt.cardBorder}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flex: 1,
          }}
        >
          <PrecisionManufacturingOutlinedIcon
            sx={{ fontSize: 18, color: dt.blue }}
          />
          <Box
            sx={{
              fontSize: "15px",
              fontWeight: 700,
              color: dt.textPrimary,
              fontFamily: dt.font,
            }}
          >
            {translate("profile.machinery")} ({totalCount})
          </Box>
        </Box>

        {/* Search */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            border: `1px solid ${dt.cardBorder}`,
            borderRadius: dt.radiusSm,
            padding: "6px 12px",
            background: dt.inputBg,
            width: isPhone ? "100%" : "220px",
          }}
        >
          <SearchOutlinedIcon sx={{ fontSize: 16, color: dt.textMuted }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={translate("search.machines")}
            style={{
              border: "none",
              outline: "none",
              background: "transparent",
              flex: 1,
              fontSize: "13px",
              fontFamily: dt.font,
              color: dt.textPrimary,
            }}
          />
        </Box>
      </Box>

      {/* Filter pills */}
      {typeFilters.length > 1 && (
        <Box
          sx={{
            display: "flex",
            gap: "8px",
            flexWrap: "wrap",
            padding: isPhone ? "12px 16px" : "12px 24px",
            borderBottom: `1px solid ${dt.cardBorder}`,
          }}
        >
          <Box
            onClick={() => setTypeFilter(null)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              padding: "4px 12px",
              borderRadius: "20px",
              fontSize: "12px",
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: dt.font,
              transition: "all 0.2s",
              background: typeFilter === null ? dt.blue : "transparent",
              color: typeFilter === null ? "#fff" : dt.textSecondary,
              border: `1px solid ${typeFilter === null ? dt.blue : dt.cardBorder}`,
              "&:hover": {
                borderColor: dt.blue,
                color: typeFilter === null ? "#fff" : dt.blue,
              },
            }}
          >
            {translate("filters.all")}
          </Box>
          {typeFilters.map((tf) => (
            <Box
              key={tf.id}
              onClick={() =>
                setTypeFilter(typeFilter === tf.id ? null : tf.id)
              }
              sx={{
                display: "inline-flex",
                alignItems: "center",
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: dt.font,
                transition: "all 0.2s",
                background: typeFilter === tf.id ? dt.blue : "transparent",
                color: typeFilter === tf.id ? "#fff" : dt.textSecondary,
                border: `1px solid ${typeFilter === tf.id ? dt.blue : dt.cardBorder}`,
                "&:hover": {
                  borderColor: dt.blue,
                  color: typeFilter === tf.id ? "#fff" : dt.blue,
                },
              }}
            >
              {tf.name}
            </Box>
          ))}
        </Box>
      )}

      {/* Machine grid */}
      <Box sx={{ padding: isPhone ? "12px 16px" : "16px 24px" }}>
        {displayCount === 0 ? (
          // Empty state
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "12px",
              py: "40px",
            }}
          >
            <PrecisionManufacturingOutlinedIcon
              sx={{ fontSize: 40, color: dt.textMuted, opacity: 0.5 }}
            />
            <Box
              sx={{
                fontSize: "14px",
                color: dt.textMuted,
                fontFamily: dt.font,
              }}
            >
              {translate("hero.noMachinesListed")}
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(auto-fill, minmax(280px, 1fr))",
              },
              gap: "8px",
            }}
          >
            {displayedMachines.map((machine) => (
              <CompactMachineCard key={machine.id} machine={machine} showActions={false} />
            ))}
          </Box>
        )}

        {/* "View all" button */}
        {isCapped && (
          <Box
            onClick={() => navigate(`/machinery?company=${companyId}`)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              mt: "16px",
              py: "10px",
              borderRadius: dt.radiusSm,
              border: `1px solid ${dt.cardBorder}`,
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: 600,
              color: dt.blue,
              fontFamily: dt.font,
              transition: "all 0.2s",
              "&:hover": {
                borderColor: dt.blue,
                background: dt.blueSoft,
              },
            }}
          >
            {translate("hero.viewAllMachines", { count: totalCount })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CompanyMachinery;
